import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import "./Contact.css";
import ThreeBackground from "../../components/common/ThreeBackground";
import Icon from "../../components/common/Icon";
import { scheduleMeeting } from "../../config/api";
import { contact } from "../../Data/siteContent";
import { useSeo, SEO } from "../../config/seo";

const ease = [0.21, 0.8, 0.25, 1];

const REASONS = [
  { title: "Clarity From Day One", body: "No vague quotes — you get a scoped plan within 48 hours.", icon: "spark" },
  { title: "Dedicated Project Team", body: "One point of contact, daily updates, no disappearing devs.", icon: "team" },
  { title: "On-Time, Every Time", body: "Milestone-based delivery with transparent tracking.", icon: "check" },
];

const EMPTY = { fullname: "", email: "", phonenumber: "", project: "", description: "" };

export default function Contact() {
  useSeo(SEO.contact);
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState({ s: "idle", m: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
    const project = new URLSearchParams(window.location.search).get("project");
    if (project) setForm((f) => ({ ...f, project }));
  }, []);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ s: "loading", m: "" });
    try {
      await scheduleMeeting(form);
      setStatus({ s: "ok", m: "We'll be in touch within 24 hours!" });
      setForm(EMPTY);
    } catch (err) {
      setStatus({ s: "err", m: "Something went wrong. Please try again or email us directly." });
    }
  };

  const socials = [
    { key: "instagram", href: contact.socials.instagram },
    { key: "facebook", href: contact.socials.facebook },
    { key: "youtube", href: contact.socials.youtube },
    { key: "linkedin", href: contact.socials.linkedin },
  ];

  return (
    <main className="cv-home cv-contact-page">
      {/* A. Hero */}
      <header className="cv-ct-hero">
        <ThreeBackground variant="constellation" />
        <div className="cv-ct-hero__inner">
          <motion.span className="cv-eyebrow" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <span className="dot" /> Let's talk
          </motion.span>
          <motion.h1 className="cv-ct-hero__title" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08, ease }}>
Tell us what&rsquo;s <span className="cv-gradient-text">not working</span>
          </motion.h1>
          <motion.p className="cv-ct-hero__sub" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.16, ease }}>
Describe the problem and which systems you run. You&rsquo;ll get a scoped plan back
            within 48 hours &mdash; what we&rsquo;d build, how it connects, and what it costs.
          </motion.p>
        </div>
      </header>

      {/* B. Three reasons (flip cards) */}
      <section className="cv-section cv-section--tight">
        <div className="cv-ct-reasons">
          {REASONS.map((r, i) => (
            <motion.div
              key={r.title}
              className="cv-flip"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.1, ease }}
            >
              <div className="cv-flip__inner">
                <div className="cv-flip__face cv-flip__front cv-glass">
                  <span className="cv-flip__ic"><Icon name={r.icon} size={24} /></span>
                  <h3>{r.title}</h3>
                  <span className="cv-flip__hint">Hover to read →</span>
                </div>
                <div className="cv-flip__face cv-flip__back cv-glass">
                  <p>{r.body}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* C. Info + Form */}
      <section className="cv-section cv-section--tight">
        <div className="cv-ct-grid">
          <motion.div className="cv-ct-info" initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }}>
            <h2>Reach the studio</h2>
            <div className="cv-ct-info__rows">
              <span className="cv-ct-info__row"><Icon name="map" size={18} /> {contact.address}, {contact.region}</span>
              <a className="cv-ct-info__row" href={`mailto:${contact.email}`}><Icon name="mail" size={18} /> {contact.email}</a>
              <a className="cv-ct-info__row" href={`tel:${contact.phone}`}><Icon name="phone" size={18} /> {contact.phoneDisplay}</a>
              <span className="cv-ct-info__row"><Icon name="clock" size={18} /> {contact.hours}</span>
            </div>
            <div className="cv-ct-info__socials">
              {socials.map((s) => (
                <a key={s.key} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.key}><Icon name={s.key} size={18} /></a>
              ))}
            </div>
            <div className="cv-ct-info__call">
              <h3>Prefer to talk?</h3>
              <p>Call or email us directly for a 30-minute discovery call — no commitment, no deck.</p>
              <a href={`tel:${contact.phone}`} className="cv-btn cv-btn--sm cv-btn--ghost">
                <Icon name="phone" size={16} /> {contact.phoneDisplay}
              </a>
            </div>

            <Link to="/products" className="cv-ct-info__group">
              <Icon name="spark" size={15} /> Part of Cafevista Pvt Ltd — meet the group
            </Link>
          </motion.div>

          <motion.form className="cv-ct-form cv-glass" onSubmit={submit} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }}>
            <div className="cv-field">
              <label htmlFor="ct-name">Full Name</label>
              <input id="ct-name" name="fullname" value={form.fullname} onChange={update} required placeholder="Jane Doe" />
            </div>
            <div className="cv-field-row">
              <div className="cv-field">
                <label htmlFor="ct-email">Email</label>
                <input id="ct-email" type="email" name="email" value={form.email} onChange={update} required placeholder="you@company.com" />
              </div>
              <div className="cv-field">
                <label htmlFor="ct-phone">Phone</label>
                <input id="ct-phone" name="phonenumber" value={form.phonenumber} onChange={update} required placeholder="+92 …" />
              </div>
            </div>
            <div className="cv-field">
              <label htmlFor="ct-project">Project Name</label>
              <input id="ct-project" name="project" value={form.project} onChange={update} required placeholder="e.g. AI chatbot for support" />
            </div>
            <div className="cv-field">
              <label htmlFor="ct-desc">Project Description</label>
              <textarea id="ct-desc" name="description" value={form.description} onChange={update} required rows={4} placeholder="Tell us about your idea, goals and timeline…" />
            </div>
            <button type="submit" className="cv-btn cv-ct-form__submit" disabled={status.s === "loading"}>
              {status.s === "loading" ? <span className="cv-spinner" /> : <>Send Message <Icon name="arrow" size={18} className="arrow" /></>}
            </button>
            {status.s === "ok" && (
              <motion.p className="cv-form-note cv-form-note--ok" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <Icon name="check" size={16} /> {status.m}
              </motion.p>
            )}
            {status.s === "err" && <p className="cv-form-note cv-form-note--err">{status.m}</p>}
          </motion.form>
        </div>
      </section>

    </main>
  );
}
